/**
 * AppOnboardingExpanded — Full step view (dumb component).
 *
 * Rendered above AppOnboardingPanel when isExpanded is true.
 * Uses Framer Motion for the slide-up entrance/exit animation.
 *
 * Props
 * -----
 * filteredSteps    Array   Steps with { ...config, completed: bool }
 * completedCount   number  Backend-confirmed completed step count (drives badge/ring/bar)
 * currentStepIndex number  Navigation position (drives active dot + which card shows)
 * trialConfig      object  { durationHours, creditAmount, creditCurrency, trialStartedAt }
 * stepCallbacks    object  { [ctaKey]: fn } — resolved callback per step
 * onSkipStep       fn      (stepId) => void
 * onToggle         fn      close the expanded view
 */
import moment from "moment-timezone";
import React from "react";
import { CommonImage, CommonText } from "styledComponents/common";

import ChevronIcon_SVG from "../../assets/img/icons/chevron.svg";
import GiftCreditIcon from "../../assets/img/icons/gift.png";
import GiftCreditIconInactive from "../../assets/img/icons/gift_10_expired.png";
import useCountdown from "../../customHooks/useCountdown";
import { useResponsive } from "../../customHooks/useResponsive";
import { AnimatePresence } from "../AuthNew/shared/motionUtils";
import { motion } from "../AuthNew/shared/motionUtils";
import {
  BadgeAndInfoGroup,
  ChevronButton,
  ChevronIcon,
  ContinueButton,
  CreditCopy,
  CreditHighlight,
  ExpandedContainer,
  ExpandedHeader,
  ExpandedHeaderTitle,
  ExpandedInner,
  ExpandedSubtitle,
  ExpandedTitle,
  GiftIcon,
  MobileChevronIcon,
  MobileCollapseButton,
  MobileCreditText,
  MobileGiftIcon,
  MobileTrialBanner,
  PanelLeftGroup,
  PanelRightGroup,
  ProgressBarFill,
  ProgressBarTrack,
  StepBadge,
  StepBadgeNumber,
  StepBadgeTotal,
  StepDot,
  StepDotLabel,
  StepDotWrapper,
  StepDotsRow,
  TabletBadgeNumber,
  TabletBadgeTotal,
  TabletBanner,
  TabletCollapseButton,
  TabletDivider,
  TabletGiftIcon,
  TabletHeaderRow,
  TabletSmallBadge,
  TabletTimerChip,
  TimerBox,
  TimerDivider,
  TimerValue,
  TimerValueWrapper,
  panelSlideUp,
  stepSlideIn,
} from "./shared/appOnboardingStyles";
import StepAddCustomer from "./steps/StepAddCustomer";
import StepAddEmployees from "./steps/StepAddEmployees";
import StepAddTeam from "./steps/StepAddTeam";
import StepCompleteJob from "./steps/StepCompleteJob";
import StepCreateJob from "./steps/StepCreateJob";
import StepInstallApp from "./steps/StepInstallApp";
import StepScheduleJob from "./steps/StepScheduleJob";
import StepSendInvoice from "./steps/StepSendInvoice";

// ---------------------------------------------------------------------------
// Step component map — update here when adding / removing steps
// ---------------------------------------------------------------------------
const STEP_COMPONENT_MAP = {
  add_employees: StepAddEmployees,
  add_team: StepAddTeam,
  add_customer: StepAddCustomer,
  create_job: StepCreateJob,
  create_schedule: StepScheduleJob,
  install_mobile_app: StepInstallApp,
  complete_job: StepCompleteJob,
  create_invoice: StepSendInvoice,
};

const pad = n => String(n).padStart(2, "0");

const AppOnboardingExpanded = React.forwardRef(
  (
    {
      filteredSteps,
      completedCount,
      currentStepIndex,
      onPrevStep,
      onNextStep,
      onStepDotClick,
      totalSteps,
      trialConfig,
      stepCallbacks,
      onSkipStep,
      onToggle,
      isMobile,
      isTablet,
      selectedOrganization,
      completeAppOnboardingStep,
    },
    ref,
  ) => {
    // Two independent concerns:
    //   completedCount (prop) — backend-confirmed done count → badge, ring, progress bar,
    //                           tick icons, connecting lines. Never affected by navigation.
    //   currentStepIndex      — navigation only → which card is shown, which dot is active.
    const progressPercent =
      totalSteps > 0 ? (completedCount / totalSteps) * 100 : 0;

    // Resolve step using currentStepIndex (what user is viewing)
    const currentStep = filteredSteps[currentStepIndex];

    const isMobileView = isMobile || isTablet;

    // Convert trialStartedAt from UTC to selected timezone or fallback to moment default timezone
    // Backend returns ISO 8601 format: "2026-03-30T11:35:35.922376" (assumed UTC)
    const convertedStartTime = trialConfig?.trialStartedAt
      ? selectedOrganization?.timezone
        ? moment
            .utc(trialConfig.trialStartedAt)
            .tz(selectedOrganization.timezone)
        : moment(trialConfig.trialStartedAt)
      : null;

    // Timer for the header area
    const durationMs = trialConfig
      ? trialConfig.durationHours * 60 * 60 * 1000
      : null;
    const { hours, minutes, seconds, isExpired } = useCountdown({
      startTime: convertedStartTime?.format() ?? null,
      durationMs,
    });
    const showTimer = trialConfig && !isExpired;

    const renderStepDots = (isMobile, isTablet) =>
      filteredSteps.map((step, index) => {
        // Determine dot state: completed, skipped, active, pending
        const dotState =
          step.completed && index === currentStepIndex
            ? "completed-active"
            : step.completed
            ? "completed"
            : step.skipped && index === currentStepIndex
            ? "skipped-active"
            : step.skipped
            ? "skipped"
            : index === currentStepIndex
            ? "active"
            : "pending";

        const nextStep = filteredSteps[index + 1];
        const showConnector = step.completed && nextStep?.completed === true;

        return (
          <StepDotWrapper
            isMobileView={isMobileView}
            key={step.id}
            $showConnector={showConnector}
          >
            <StepDot
              $state={dotState}
              aria-label={step.title}
              aria-current={index === currentStepIndex ? "step" : undefined}
              type="button"
              onClick={() => onStepDotClick(index)}
            >
              {step.completed ? (
                <svg
                  width="13"
                  height="16"
                  viewBox="0 0 12 10"
                  fill="none"
                  aria-hidden="true"
                  style={{ display: "block", position: "relative", zIndex: 2 }}
                >
                  <path
                    d="M1 5L4.5 8.5L11 1.5"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <span>{step.stepNumber}</span>
              )}
            </StepDot>
            {(!isMobile || dotState === "active") && (
              <StepDotLabel $state={dotState}>{step.title}</StepDotLabel>
            )}
          </StepDotWrapper>
        );
      });

    // Resolve the current step component to render
    const CurrentStepComponent = currentStep
      ? STEP_COMPONENT_MAP[currentStep.id]
      : null;

    return (
      <ExpandedContainer
        ref={ref}
        key="expanded"
        initial={panelSlideUp.initial}
        animate={panelSlideUp.animate}
        exit={panelSlideUp.exit}
      >
        <ExpandedInner>
          {isMobileView && (
            <StepDotsRow>{renderStepDots(isMobile, isTablet)}</StepDotsRow>
          )}
          {/* Header row */}
          {!isMobileView && (
            <ExpandedHeader>
              {/* Badge and Step Info group */}
              <PanelLeftGroup style={{ margin: "0px" }}>
                <BadgeAndInfoGroup>
                  {/* Step progress badge */}
                  <motion.div
                    key={`expanded-desktop-badge-${completedCount}`}
                    animate={{ x: [0, -5, 5, -5, 5, 0] }}
                    transition={{
                      duration: 0.4,
                      ease: "linear",
                      repeat: 2,
                    }}
                    style={{ display: "inline-block" }}
                  >
                    <StepBadge
                      aria-label={`${completedCount} of ${totalSteps} steps completed`}
                    >
                      {/* SVG Progress Ring */}
                      <svg
                        width="60"
                        height="60"
                        viewBox="0 0 60 60"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ position: "absolute", left: 0, top: 0 }}
                      >
                        {/* Background circle */}
                        <circle cx="30" cy="30" r="30" fill="#EDF8FE" />

                        {/* Progress ring — fills based on backend-confirmed completedCount only */}
                        <circle
                          cx="30"
                          cy="30"
                          r="27"
                          fill="none"
                          stroke="#2266EF"
                          strokeWidth="5"
                          strokeDasharray={2 * Math.PI * 27}
                          strokeDashoffset={
                            2 * Math.PI * 27 * (1 - completedCount / totalSteps)
                          }
                          strokeLinecap="round"
                          transform="rotate(-90 30 30)"
                          style={{ transition: "stroke-dashoffset 0.3s ease" }}
                        />
                      </svg>

                      <StepBadgeNumber style={{ display: "inline-block" }}>
                        {completedCount}
                      </StepBadgeNumber>
                      <StepBadgeTotal style={{ display: "inline-block" }}>
                        /{totalSteps}
                      </StepBadgeTotal>
                    </StepBadge>
                  </motion.div>
                  {/* Expanded title and subtitle */}
                  <ExpandedHeaderTitle>
                    <ExpandedTitle>Get up and running in no time</ExpandedTitle>
                    <ExpandedSubtitle>
                      Set up your app, understand how it works, and get your
                      team ready.
                    </ExpandedSubtitle>
                  </ExpandedHeaderTitle>
                </BadgeAndInfoGroup>
              </PanelLeftGroup>
              <PanelRightGroup style={{ margin: "0px 0 auto auto " }}>
                <TimerBox
                // style={{
                //   visibility:
                //     !trialConfig || isExpired ? "hidden" : "visible",
                // }}
                >
                  {/* Gift icon */}
                  <GiftIcon>
                    <CommonImage
                      src={isExpired ? GiftCreditIconInactive : GiftCreditIcon}
                      alt="Gift"
                    />
                  </GiftIcon>
                  {!isExpired && (
                    <CreditCopy>
                      Complete these steps in 24 hours to get a{" "}
                      <CreditHighlight>
                        {trialConfig?.creditCurrency ?? "$"}
                        {trialConfig?.creditAmount ?? 20} credit
                      </CreditHighlight>{" "}
                      when upgrading
                    </CreditCopy>
                  )}
                  {isExpired && (
                    <CommonText color="#000E2A">
                      Your {trialConfig?.creditCurrency ?? "$"}
                      {trialConfig?.creditAmount ?? 20} onboarding reward has
                      expired, but you can still complete these steps to get
                      started.
                    </CommonText>
                  )}
                  <TimerDivider />
                  {/* {showTimer && ( */}
                  <TimerValueWrapper>
                    <TimerValue isExpired={isExpired}>
                      {pad(hours)}:{pad(minutes)}:{pad(seconds)}
                    </TimerValue>
                  </TimerValueWrapper>
                  {/* )} */}
                </TimerBox>

                {/* Collapse chevron */}
                <ChevronButton
                  $isExpanded
                  aria-label="Collapse onboarding panel"
                  onClick={onToggle}
                  isMobileView={isMobileView}
                >
                  <ChevronIcon
                    as={CommonImage}
                    src={ChevronIcon_SVG}
                    alt="Chevron"
                  />
                </ChevronButton>
              </PanelRightGroup>
            </ExpandedHeader>
          )}

          {/* Progress bar */}
          {!isMobileView && (
            <ProgressBarTrack>
              <ProgressBarFill $percent={100} />
            </ProgressBarTrack>
          )}

          {/* Active step card — animated between steps */}
          <AnimatePresence mode="wait" exitBeforeEnter>
            {CurrentStepComponent && (
              <motion.div
                key={currentStep.id}
                initial={stepSlideIn.initial}
                animate={stepSlideIn.animate}
                exit={stepSlideIn.exit}
              >
                <CurrentStepComponent
                  stepNumber={currentStep.stepNumber}
                  totalSteps={totalSteps}
                  onCtaClick={stepCallbacks[currentStep.ctaKey]}
                  onSkip={() => onSkipStep(currentStep.id)}
                  title={currentStep.title}
                  description={currentStep.description}
                  ctaLabel={currentStep.ctaLabel}
                  selectedOrganization={selectedOrganization}
                  completeAppOnboardingStep={completeAppOnboardingStep}
                />
              </motion.div>
            )}
          </AnimatePresence>
          {/* Progress bar */}
          {!isMobileView && (
            <ProgressBarTrack style={{ marginTop: "32px" }}>
              <ProgressBarFill $percent={100} />
            </ProgressBarTrack>
          )}

          {/* Step dots navigation */}
          {!isMobileView && (
            <StepDotsRow>{renderStepDots(isMobile, isTablet)}</StepDotsRow>
          )}

          {/* Mobile Trial Banner */}
          {isMobile && (
            <MobileTrialBanner>
              <MobileGiftIcon>
                <CommonImage
                  src={isExpired ? GiftCreditIconInactive : GiftCreditIcon}
                  alt="Gift"
                />
              </MobileGiftIcon>
              {!isExpired && (
                <MobileCreditText>
                  Complete these steps in 24 hours to get a{" "}
                  <CreditHighlight>
                    {trialConfig?.creditCurrency ?? "$"}
                    {trialConfig?.creditAmount ?? 20} credit
                  </CreditHighlight>{" "}
                  when upgrading
                </MobileCreditText>
              )}
              {isExpired && (
                <CommonText color="#000E2A">
                  Your {trialConfig?.creditCurrency ?? "$"}
                  {trialConfig?.creditAmount ?? 20} onboarding reward has
                  expired, but you can still complete these steps to get
                  started.
                </CommonText>
              )}
              <MobileCollapseButton
                $isExpanded
                aria-label="Collapse onboarding panel"
                onClick={onToggle}
              >
                <MobileChevronIcon
                  as={CommonImage}
                  src={ChevronIcon_SVG}
                  alt="Chevron"
                />
              </MobileCollapseButton>
            </MobileTrialBanner>
          )}

          {isTablet && (
            <div style={{ marginTop: "24px" }}>
              <TabletHeaderRow>
                <motion.div
                  key={`expanded-tablet-badge-${completedCount}`}
                  animate={{ x: [0, -5, 5, -5, 5, 0] }}
                  transition={{
                    duration: 0.4,
                    ease: "linear",
                    repeat: 2,
                  }}
                  style={{ display: "inline-block" }}
                >
                  <TabletSmallBadge
                    aria-label={`${completedCount} of ${totalSteps} steps completed`}
                  >
                    <svg
                      width="60"
                      height="60"
                      viewBox="0 0 60 60"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ position: "absolute", left: 0, top: 2 }}
                    >
                      <circle cx="30" cy="30" r="30" fill="#EDF8FE" />
                      <circle
                        cx="30"
                        cy="30"
                        r="27"
                        fill="none"
                        stroke="#2266EF"
                        strokeWidth="5"
                        strokeDasharray={2 * Math.PI * 27}
                        strokeDashoffset={
                          2 * Math.PI * 27 * (1 - completedCount / totalSteps)
                        }
                        strokeLinecap="round"
                        transform="rotate(-90 30 30)"
                        style={{ transition: "stroke-dashoffset 0.3s ease" }}
                      />
                    </svg>
                    <TabletBadgeNumber style={{ display: "inline-block" }}>
                      {completedCount}
                    </TabletBadgeNumber>
                    <TabletBadgeTotal style={{ display: "inline-block" }}>
                      /{totalSteps}
                    </TabletBadgeTotal>
                  </TabletSmallBadge>
                </motion.div>

                <TabletBanner
                // style={{
                //   visibility:
                //     !trialConfig || isExpired ? "hidden" : "visible",
                // }}
                >
                  <TabletGiftIcon>
                    <CommonImage
                      src={isExpired ? GiftCreditIconInactive : GiftCreditIcon}
                      alt="Gift"
                    />
                  </TabletGiftIcon>

                  {!isExpired && (
                    <MobileCreditText style={{ flex: 1 }}>
                      Complete these steps in 24 hours to get a{" "}
                      <CreditHighlight>
                        {trialConfig?.creditCurrency ?? "$"}
                        {trialConfig?.creditAmount ?? 20} credit
                      </CreditHighlight>{" "}
                      when upgrading
                    </MobileCreditText>
                  )}

                  {isExpired && (
                    <CommonText color="#000E2A">
                      Your {trialConfig?.creditCurrency ?? "$"}
                      {trialConfig?.creditAmount ?? 20} onboarding reward has
                      expired, but you can still complete these steps to get
                      started.
                    </CommonText>
                  )}

                  <TabletDivider />

                  <TabletTimerChip isExpired={isExpired}>
                    {/* {showTimer && ( */}
                      <span>
                        {pad(hours)}:{pad(minutes)}:{pad(seconds)}
                      </span>
                    {/* )} */}
                  </TabletTimerChip>
                </TabletBanner>

                <TabletCollapseButton
                  $isExpanded={true}
                  aria-label="Collapse onboarding panel"
                  onClick={e => {
                    e.stopPropagation();
                    onToggle();
                  }}
                >
                  <MobileChevronIcon
                    as={CommonImage}
                    src={ChevronIcon_SVG}
                    alt="Chevron"
                  />
                </TabletCollapseButton>
              </TabletHeaderRow>
            </div>
          )}
        </ExpandedInner>
      </ExpandedContainer>
    );
  },
);

export default AppOnboardingExpanded;
